import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
  sendPasswordResetEmail,
  sendEmailVerification,
  reload,
  signInWithPopup,
  GoogleAuthProvider,
  linkWithPopup,
  linkWithCredential,
  EmailAuthProvider,
  updatePassword,
  reauthenticateWithCredential
} from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, firestore, functions } from '../config/firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingVerification, setSendingVerification] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        let extra = { id: firebaseUser.uid, role: 'customer' };
        try {
          const userDoc = await getDoc(doc(firestore, 'users', firebaseUser.uid));
          if (userDoc.exists()) {
            extra = { id: firebaseUser.uid, ...userDoc.data() };
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
        // Sync emailVerified field
        if (extra.emailVerified !== firebaseUser.emailVerified) {
          try {
            await updateDoc(doc(firestore, 'users', firebaseUser.uid), { emailVerified: firebaseUser.emailVerified, updatedAt: new Date() });
            extra.emailVerified = firebaseUser.emailVerified;
          } catch (e) {
            // ignore
          }
        }
        setUserData(extra);
      } else {
        setUser(null);
        setUserData(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async (email, password) => {
    setLoading(true);
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result.user;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (email, password, displayName) => {
    setLoading(true);
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      // Update profile with display name
      await updateProfile(result.user, { displayName });
      // Ensure Auth state is updated before calling Cloud Function
      await auth.currentUser.reload();
      // Now call initializeUser Cloud Function (displayName will be present)
      try {
        const initializeUser = httpsCallable(functions, 'initializeUser');
        await initializeUser();
      } catch (error) {
        console.error('Error initializing user data:', error);
      }
      // Send verification email
      try { await sendEmailVerification(result.user); } catch (e) { console.warn('Failed to send verification email on register', e); }
      return result.user;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  };

  const getAuthToken = async () => {
    if (user) {
      return await user.getIdToken();
    }
    return null;
  };

  const isAdmin = () => {
    return userData?.role === 'admin';
  };

  const updateUserProfile = async (profileData) => {
    try {
      if (!user) throw new Error('No user logged in');
      
      // Update user document in Firestore
      const userRef = doc(firestore, 'users', user.uid);
      await updateDoc(userRef, {
        ...profileData,
        updatedAt: new Date()
      });
      
      // Reload user data
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        setUserData(userDoc.data());
      }
      
      return true;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  };

  const sendVerificationEmailNow = async () => {
    if (!user) throw new Error('No user');
    if (user.emailVerified) return false;
    setSendingVerification(true);
    try {
      await sendEmailVerification(user);
      return true;
    } catch (e) {
      console.error('Send verification email error', e);
      throw e;
    } finally { setSendingVerification(false); }
  };

  const refreshEmailVerificationStatus = async () => {
    if (!user) return false;
    try {
      await reload(user);
      if (auth.currentUser?.emailVerified) {
        // Update Firestore if needed
        if (!userData?.emailVerified) {
          try { await updateDoc(doc(firestore, 'users', user.uid), { emailVerified: true, updatedAt: new Date() }); } catch {}
          setUserData(prev => prev ? { ...prev, emailVerified: true } : prev);
        }
        setUser(auth.currentUser);
        return true;
      }
      return false;
    } catch (e) {
      console.error('Refresh email verification error', e);
      return false;
    }
  };

  // Google Authentication
  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account'
    });
    
    try {
      const result = await signInWithPopup(auth, provider);
      // Initialize user data if this is a new user
      if (result._tokenResponse?.isNewUser) {
        try {
          const initializeUser = httpsCallable(functions, 'initializeUser');
          await initializeUser();
        } catch (error) {
          console.error('Error initializing user data:', error);
        }
      }
      return result.user;
    } catch (error) {
      console.error('Google sign-in error:', error);
      throw error;
    }
  };

  // Link existing account with Google
  const linkWithGoogle = async () => {
    if (!user) throw new Error('No user logged in');
    
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account'
    });
    
    try {
      const result = await linkWithPopup(user, provider);
      setUser(result.user);
      return result.user;
    } catch (error) {
      console.error('Google account linking error:', error);
      throw error;
    }
  };

  // Password Management
  const createPassword = async (password) => {
    if (!user) throw new Error('No user logged in');
    
    // Check if user already has password (email/password provider)
    const hasPasswordProvider = user.providerData.some(
      provider => provider.providerId === 'password'
    );
    
    if (hasPasswordProvider) {
      throw new Error('User already has a password. Use updatePassword instead.');
    }
    
    try {
      const credential = EmailAuthProvider.credential(user.email, password);
      const result = await linkWithCredential(user, credential);
      setUser(result.user);
      return result.user;
    } catch (error) {
      console.error('Create password error:', error);
      throw error;
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    if (!user) throw new Error('No user logged in');
    
    try {
      // Re-authenticate user first
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      
      // Update password
      await updatePassword(user, newPassword);
      return true;
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  };

  const hasPasswordProvider = () => {
    if (!user) return false;
    return user.providerData.some(provider => provider.providerId === 'password');
  };

  const hasGoogleProvider = () => {
    if (!user) return false;
    return user.providerData.some(provider => provider.providerId === 'google.com');
  };

  const value = {
    currentUser: user,  // Export as currentUser for consistency
    user,
    userData,
    loading,
    login,
    register,
    logout,
    resetPassword,
    getAuthToken,
    isAdmin,
    updateUserProfile,
    sendVerificationEmailNow,
    refreshEmailVerificationStatus,
    sendingVerification,
    // Google Auth methods
    signInWithGoogle,
    linkWithGoogle,
    // Password management
    createPassword,
    changePassword,
    hasPasswordProvider,
    hasGoogleProvider
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
